/** Up to two uppercase initials for a person or entity name ("Анна Петрова" → "АП"). */
export function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name
    .trim()
    .split(/[\s\-_.]+/u)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : (parts[0]?.[1] ?? '');
  return (first + second).toLocaleUpperCase();
}

/** Deterministic 32-bit FNV-1a hash of a string. */
export function hashString(value: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** Hues (OKLCH) used for avatar tints; chosen to be distinguishable in light and dark themes. */
const AVATAR_HUES = [264, 292, 330, 12, 45, 85, 145, 175, 205, 235] as const;

/** Stable hue for a user/entity id, used as `--avatar-h`. */
export function avatarHue(seed: string): number {
  return AVATAR_HUES[hashString(seed) % AVATAR_HUES.length] ?? 264;
}

/** Lower case, `ё` = `е`, collapsed whitespace: how palette entries and queries are compared. */
export function foldForMatch(value: string): string {
  return value.replace(/\s+/gu, ' ').trim().toLocaleLowerCase().replaceAll('ё', 'е');
}

/**
 * Whether a static palette entry (an action like "New task") matches the query: an empty query matches everything,
 * otherwise the label or one of the keywords must contain it.
 */
export function matchesQuery(query: string, ...texts: (string | null | undefined)[]): boolean {
  const q = foldForMatch(query);
  return !q || texts.some((text) => !!text && foldForMatch(text).includes(q));
}

/** Collapses whitespace and trims; returns undefined for empty input. */
export function normalizeQuery(value: string): string | undefined {
  const trimmed = value.replace(/\s+/gu, ' ').trim();
  return trimmed.length > 0 ? trimmed : undefined;
}
